import React, { useState, useEffect, useRef } from 'react';
import '@aerialist7/react-dates/initialize';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DayPickerRangeController, DayPickerSingleDateController, isSameDay } from '@aerialist7/react-dates';
import { START_DATE, END_DATE } from '@aerialist7/react-dates/constants';

import * as Styled from './DatePicker.styles';
import { DatePickerField } from './DatePickerField';
import { SVGIcon } from '../SVGIcon';
import { Adaptive } from '../../Utility/Adaptive';
import { colors } from '../../css';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

function DatePicker(props) {
  const {
    allowPastDates,
    children,
    labelText,
    inputBorderType,
    blockedDays,
    mobileDaySize,
    onDatesChangeHook,
    initialStartDate,
    initialEndDate,
    disclaimerCopy,
    open,
    fullScreen,
    noInputs,
    pickerPosition,
    disableDatePicker,
    noArrow,
    onFocusHook,
    isSingleDate,

    isDayHighlighted,
    keepOpenOnDateSelect,
    hideKeyboardShortcutsPanel,
    navNext,
    navPrev,
    monthFormat,
    minimumNights
  } = props;

  const LL_DATE_FORMAT = 'YYYY-MM-DD';

  const PRETTY_DATE_FORMAT = 'MMM DD, YYYY';

  const LONG_DATE_FORMAT = 'MMM D, YYYY';

  const resolveIncomingDate = (dateString) => {
    if (typeof dateString !== 'string') {
      return null;
    }

    if (dateString instanceof moment) {
      return dateString;
    }

    const momentDate = moment(dateString, LL_DATE_FORMAT);
    return momentDate.isValid() ? momentDate : null;
  };

  const [focusedInput, setFocusedInput] = useState(initialStartDate && !initialEndDate ? END_DATE : START_DATE);
  const [startDate, setStartDate] = useState(resolveIncomingDate(initialStartDate));
  const [endDate, setEndDate] = useState(resolveIncomingDate(initialEndDate));
  const [visible, setVisible] = useState(open);

  const node = useRef(undefined);

  const closeDayPicker = () => {
    if (visible) {
      if (!(startDate && endDate)) {
        setVisible(false);
        setStartDate(undefined);
        setEndDate(undefined);
        setFocusedInput(START_DATE);
      }

      setVisible(false);
    }
  };

  const handleClickListener = ({ target }) => {
    if (!node.current.contains) {
      return false;
    }

    if (node.current.contains(target)) {
      return false;
    }

    if (!isSingleDate) {
      closeDayPicker();
    }

    return true;
  };

  useEffect(() => {
    if (document) {
      document.addEventListener('mousedown', handleClickListener, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickListener, true);
    };
  }, []);

  const resolveDateStrings = (format = LL_DATE_FORMAT) => [
    startDate ? startDate.format(format) : undefined,
    endDate ? endDate.format(format) : undefined
  ];

  const onDatesChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setStartDate(newStartDate);
    setEndDate(newStartDate && !newStartDate.isSame(startDate) && endDate ? null : newEndDate);

    const resolvedDates = resolveDateStrings();
    // const { startDate: newStartDate, endDate: newEndDate } = state;
    if (newStartDate && newEndDate) closeDayPicker();
    onDatesChangeHook(resolvedDates, resolveDateStrings);
  };

  const onSingleDateChange = (value) => {
    setStartDate(value);
    if (value && value !== startDate) {
      onDatesChangeHook(value.format(LL_DATE_FORMAT));
    }
    setVisible(false);
  };

  const onFocusChange = (newFocusedInput) => {
    setFocusedInput(!newFocusedInput ? START_DATE : focusedInput);
  };

  const createWrapperNode = (newNode) => {
    node.current = newNode;
  };

  const resetDates = () => {
    setStartDate('');
    setEndDate('');
    setFocusedInput(START_DATE);
    onDatesChangeHook([undefined, undefined], resolveDateStrings);
  };

  const resolveBlockedDays = (currDay) => {
    const verifyKeys = (i) =>
      Object.prototype.hasOwnProperty.call(i, 'start') && Object.prototype.hasOwnProperty.call(i, 'end');

    return blockedDays.some((day) => {
      if (typeof day === 'object' && verifyKeys(day)) {
        const start = resolveIncomingDate(day.start);
        const end = resolveIncomingDate(day.end);
        return currDay.isBetween(start, end);
      }
      return isSameDay(currDay, resolveIncomingDate(day));
    });
  };

  const openDayPicker = () => {
    if (!visible) {
      setVisible(true);
    }
  };

  /**
   * Render helper methods to clean up our render()
   */
  const renderCalendarInfo = () =>
    disclaimerCopy ? (
      <Styled.CopyMini color="tertiary" align="center">
        {disclaimerCopy}
      </Styled.CopyMini>
    ) : null;

  const [startDateString, endDateString] = resolveDateStrings(PRETTY_DATE_FORMAT);
  const startDateNoYear = startDateString?.split(',')[0];
  const resolveEndDate = endDateString ? ` - ${endDateString}` : '';
  const inputValue = startDateNoYear ? `${startDateNoYear}${resolveEndDate}` : undefined;

  return (
    <Styled.Wrapper data-testid="datePickerWrapper" fullScreen={fullScreen} ref={createWrapperNode}>
      {children
        ? children({
            startDate,
            endDate,
            visible,
            resolver: resolveDateStrings,
            openPicker: openDayPicker,
            closePicker: closeDayPicker,
            resetDates
          })
        : !noInputs && (
            <DatePickerField
              borderTheme={inputBorderType}
              labelText={labelText}
              disableDatePicker={disableDatePicker}
              value={isSingleDate && startDate ? moment(startDate).format(LONG_DATE_FORMAT) : inputValue}
              onFocusHook={(e) => {
                onFocusHook(e);
                openDayPicker(e);
              }}
            />
          )}
      <Styled.DayPickerWrapper
        visible={fullScreen ? true : visible}
        focusedInput={focusedInput}
        fullScreen={fullScreen}
        pickerPosition={pickerPosition}
        noArrow={noArrow}
      >
        <Adaptive
          md={
            isSingleDate ? (
              <DayPickerSingleDateController
                key="md-breakpoint-singledatepicker"
                isDayHighlighted={isDayHighlighted}
                keepOpenOnDateSelect={keepOpenOnDateSelect}
                hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                navNext={navNext}
                navPrev={navPrev}
                monthFormat={monthFormat}
                onFocusChange={onFocusChange}
                renderCalendarInfo={renderCalendarInfo}
                isDayBlocked={blockedDays && resolveBlockedDays}
                isOutsideRange={allowPastDates && (() => false)}
                date={startDate}
                onDateChange={(dateChange) => onSingleDateChange(dateChange)}
                numberOfMonths={1}
              />
            ) : (
              <DayPickerRangeController
                key="md-breakpoint-datepicker"
                isDayHighlighted={isDayHighlighted}
                keepOpenOnDateSelect={keepOpenOnDateSelect}
                hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                navNext={navNext}
                navPrev={navPrev}
                monthFormat={monthFormat}
                minimumNights={minimumNights}
                onDatesChange={onDatesChange}
                onFocusChange={onFocusChange}
                renderCalendarInfo={renderCalendarInfo}
                focusedInput={focusedInput}
                startDate={startDate}
                endDate={endDate}
                isDayBlocked={blockedDays && resolveBlockedDays}
                isOutsideRange={allowPastDates && (() => false)}
                daySize={mobileDaySize || 30}
              />
            )
          }
          sm={
            isSingleDate ? (
              <DayPickerSingleDateController
                key="sm-breakpoint-singledatepicker"
                isDayHighlighted={isDayHighlighted}
                keepOpenOnDateSelect={keepOpenOnDateSelect}
                hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                navNext={navNext}
                navPrev={navPrev}
                monthFormat={monthFormat}
                onFocusChange={onFocusChange}
                renderCalendarInfo={renderCalendarInfo}
                isDayBlocked={blockedDays && resolveBlockedDays}
                isOutsideRange={allowPastDates && (() => false)}
                numberOfMonths={1}
              />
            ) : (
              <DayPickerRangeController
                key="sm-breakpoint-datepicker"
                isDayHighlighted={isDayHighlighted}
                keepOpenOnDateSelect={keepOpenOnDateSelect}
                hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
                navNext={navNext}
                navPrev={navPrev}
                monthFormat={monthFormat}
                minimumNights={minimumNights}
                onDatesChange={onDatesChange}
                onFocusChange={onFocusChange}
                renderCalendarInfo={renderCalendarInfo}
                focusedInput={focusedInput}
                startDate={startDate}
                endDate={endDate}
                isDayBlocked={blockedDays && resolveBlockedDays}
                isOutsideRange={allowPastDates && (() => false)}
                daySize={mobileDaySize || 30}
              />
            )
          }
        />
      </Styled.DayPickerWrapper>
    </Styled.Wrapper>
  );
}

DatePicker.propTypes = {
  /** Boolean flag to allow past dates. This overrides the default isOutsideRange method on the DayPickerRangeController */
  allowPastDates: PropTypes.bool,
  /* Array of blocked days: can be either LL Date strings or a range object with a start/end property with values that are also LL Dates. */
  blockedDays: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ start: PropTypes.string, end: PropTypes.string })])
  ),
  /* Disclaimer Copy for the bottom of the DatePicker */
  disclaimerCopy: PropTypes.string,
  /** Boolean to disable DatePicker */
  disableDatePicker: PropTypes.bool,
  /* LL Date (YYYY/MM/DD) format to seed intial start date. */
  initialStartDate: PropTypes.string,
  /* LL Date (YYYY/MM/DD) format to seed intial end date. */
  initialEndDate: PropTypes.string,
  /* Fullscreen Mode - this is mainly for the Map screen */
  fullScreen: PropTypes.bool,
  /* Label Text for the input field */
  labelText: PropTypes.string,
  /* Boolean to toggle the inputs off without doing an empty render prop function */
  noInputs: PropTypes.bool,
  /* Hook fired when onDatesChange is fired by the DayPickerRangeController  */
  onDatesChangeHook: PropTypes.func,
  /* Hook fired when the default input is focused */
  onFocusHook: PropTypes.func,
  /* Flag to toggle whether the DatePicker is visible on mount or not. Useful for the render prop implementation */
  open: PropTypes.bool,
  /* One of 'left', 'center' or 'right' to position the picker. Defaults to left. */
  pickerPosition: PropTypes.oneOf(['left', 'center', 'right']),
  /* Flag to toggle off the arrow on the top of the DatePicker pointing to the input field */
  noArrow: PropTypes.bool,
  /* Below are default props for the react-dates implementation being defined */
  // numberOfMonths: PropTypes.number,
  // isDayBlocked: PropTypes.func,
  // isOutsideRange: PropTypes.func,
  isDayHighlighted: PropTypes.func,
  keepOpenOnDateSelect: PropTypes.bool,
  hideKeyboardShortcutsPanel: PropTypes.bool,
  navNext: PropTypes.node,
  navPrev: PropTypes.node,
  monthFormat: PropTypes.string,
  /* Minimum number of days required to be selected by user */
  minimumNights: PropTypes.number,
  /* Flag for single date picker */
  isSingleDate: PropTypes.bool,

  children: ReactChildrenPropType,
  inputBorderType: PropTypes.string,
  mobileDaySize: PropTypes.number
};

DatePicker.defaultProps = {
  /**
   * Not to be confused with the onDatesChange prop for DayPicker, this prop is fired as a hook when the internal DatesChange method is executed
   * Currently returns dates in different either moment or JSDate formats depending on props.
   */
  onDatesChangeHook: () => {},
  onFocusHook: () => {},
  /** Start and End Dates */
  initialStartDate: '',
  initialEndDate: '',
  // day presentation and interaction related props
  // isDayBlocked: () => false,
  isDayHighlighted: () => false,
  // isOutsideRange: (day) => !isInclusivelyAfterDay(day, moment()),
  keepOpenOnDateSelect: false,
  // calendar presentation and interaction related props
  // numberOfMonths: 2,
  hideKeyboardShortcutsPanel: true,
  // navigation related props
  navPrev: <SVGIcon selectedIcon="arrow" position="static" rotate={90} iconColor={colors.colorBlue} />,
  navNext: <SVGIcon selectedIcon="arrow" position="static" rotate={-90} iconColor={colors.colorBlue} />,
  // internationalization
  monthFormat: 'MMMM YYYY',
  disclaimerCopy: null,
  labelText: 'Dates',
  // new props
  open: false,
  noInputs: false,
  fullScreen: false,
  blockedDays: [],
  pickerPosition: 'left',
  noArrow: false,
  disableDatePicker: false,
  allowPastDates: false,
  minimumNights: 1,
  isSingleDate: false,
  children: undefined,
  inputBorderType: '',
  mobileDaySize: -1
};

export default DatePicker;
